/**
 * Legal-Frame-Lösung (121apps-Standard): Die Rechtstexte (Impressum, AGB,
 * Datenschutz, AVV) werden NICHT mehr extern verlinkt, sondern in-app unter
 * /legal/:slug gerendert — Header/Layout von Paperball, der Text kommt zur
 * Laufzeit als content-only HTML-Fragment von 121apps.com (Single Source of
 * Truth für alle 121apps-Produkte). Siehe seiten/LegalPage.js.
 */

export const LEGAL_SLUGS = ['impressum', 'agb', 'datenschutz', 'avv'];

const LEGAL_CONTENT_LANGS = ['de', 'en', 'es', 'fr', 'it', 'nl', 'pl', 'sv', 'pt', 'no', 'da'];

/**
 * Legal texts exist on 121apps.com in German (legally binding) and as
 * convenience translations in every app language. Unknown UI languages
 * fall back to English.
 */
export function legalContentLang(lang: string | null | undefined): string {
  const base = String(lang || '').toLowerCase().split('-')[0];
  return LEGAL_CONTENT_LANGS.includes(base) ? base : 'en';
}

/**
 * Content-only fragment on 121apps.com (CORS-enabled, Cache max-age=300),
 * rendered by LegalPage inside the app's own header/layout.
 */
export function legalFragmentUrl(slug: string, lang: string | null | undefined): string {
  return `https://www.121apps.com/legal/${legalContentLang(lang)}/${slug}.html`;
}

/**
 * Standalone page on 121apps.com — used as the visible escape hatch when the
 * fragment fetch fails, and as target for links inside the fragments.
 */
export function legalExternalUrl(slug: string, lang: string | null | undefined): string {
  const contentLang = legalContentLang(lang);
  const base =
    contentLang === 'de' ? 'https://www.121apps.com' : `https://www.121apps.com/${contentLang}`;
  return `${base}/${slug}.html`;
}

/**
 * In-app routes to the legal pages. These are INTERNAL links — use
 * react-router <Link> (footers) or <a target="_blank"> where losing form
 * state would hurt (registration checkboxes).
 */
export const LEGAL_PATHS = {
  imprint: '/legal/impressum',
  terms: '/legal/agb',
  privacy: '/legal/datenschutz',
  avv: '/legal/avv',
};

/**
 * §312k / §356a BGB: die Seiten hinter den Pflicht-Schaltflächen „Verträge hier
 * kündigen" und „Vertrag widerrufen" — ohne Anmeldung erreichbar. Dieselben
 * Pfade wie `CONTRACT_PATHS` im Fundament (`packages/ui/legalLinks.ts`), damit
 * sie in jeder App der Flotte gleich heißen.
 */
export const CONTRACT_PATHS = {
  cancel: '/contracts/cancel',
  withdraw: '/contracts/withdraw',
  revoke: '/contracts/revoke',
} as const;

/**
 * Kontakt-Link im Footer (kein Rechtstext, bleibt extern). Dieselbe Adresse
 * wie das Reply-To jeder App-Mail (`EMAIL_REPLY_TO`, #644) und der
 * Brevo-Absender „Paperball" — eine Adresse für Support, nicht die
 * allgemeine Firmenadresse.
 */
export const CONTACT_URL = 'mailto:support@121apps.com';

/**
 * Pflicht-Checkbox „Risikohinweis" bei der Anmeldung (§ 2 Abs. 2 Nr. 4 WpIG).
 * EINE Quelle für alle drei Wege, auf denen ein Account entstehen kann:
 * Registrierung, Einladung annehmen, Social-Signup. Der Wortlaut greift den
 * Footer-Disclaimer und PastPerformanceDisclaimer auf, damit der Hinweis
 * überall gleich klingt — der Backend-Zeitstempel heißt
 * `users.disclaimer_accepted_at`.
 */
export const SIGNUP_DISCLAIMER_KEY = 'auth.register.accept_disclaimer';

export const SIGNUP_DISCLAIMER_DE =
  'Mir ist bewusst, dass Paperball ein Analyse-Werkzeug für historische Daten ist und keine ' +
  'Anlageberatung, Anlagevermittlung oder Finanzportfolioverwaltung im Sinne des WpIG/WpHG ' +
  'erbringt. Vergangene Wertentwicklungen sind kein zuverlässiger Indikator für künftige ' +
  'Wertentwicklungen.';

/**
 * Pflicht-Inhalt des 121-Apps-Footers (Regel #21) — EINE Quelle für beide
 * Fassungen: PublicFooter (öffentliche Seiten, Tailwind) und Layout
 * (eingeloggte App-Shell, eigene CSS-Klassen). Die beiden Fassungen dürfen
 * unterschiedlich AUSSEHEN, aber nie unterschiedliche Links oder ein anderes
 * Copyright zeigen.
 *
 * Genau das war am 29.07.2026 passiert: die Layout-Kopie zeigte
 * „© <Jahr> Paperball" und hatte keinen Kontakt-Link, obwohl Regel #21
 * „© 121 Apps GmbH" ohne Jahr und Impressum · AGB · Datenschutz · Kontakt
 * verlangt. Der Kommentar in Layout.js warnte schon vor genau dieser Drift.
 */
export const FOOTER_COPYRIGHT = '© 121 Apps GmbH';

export interface FooterEintrag {
  key: string;
  fallback: string;
  /** Internes Ziel ohne Sprach-Präfix. */
  to?: string;
  /** Externes Ziel (`mailto:`). */
  href?: string;
  /** Das Ziel gibt es hinter dem Sprach-Präfix — siehe die Liste unten. */
  lokal?: boolean;
}

/**
 * `lokal`: Das Ziel gibt es hinter dem Sprach-Präfix (`/de/deutsch/about`) —
 * jeder Footer verlinkt dann die Fassung seiner Sprache. Ohne `lokal` gibt es
 * die Seite nur flach (`/legal/:slug`: unter dem Präfix antwortet sie 404).
 * Bis 25.10.2026 zeigte „Über uns“ auf `/about`: Crawler ohne JavaScript sahen
 * dort die leere App-Hülle, und gerade die Grounding Page war für KI-Crawler
 * von keiner Seite aus erreichbar. Die Fehlerseiten setzten umgekehrt vor
 * JEDEN Link das Präfix — ihre Links auf die Rechtstexte endeten in 404.
 */
export const FOOTER_LINKS: readonly FooterEintrag[] = [
  { key: 'footer.about', fallback: 'Über uns', to: '/about', lokal: true },
  { key: 'footer.imprint', fallback: 'Impressum', to: LEGAL_PATHS.imprint },
  { key: 'footer.privacy', fallback: 'Datenschutz', to: LEGAL_PATHS.privacy },
  { key: 'footer.terms', fallback: 'AGB', to: LEGAL_PATHS.terms },
  { key: 'footer.avv', fallback: 'AVV', to: LEGAL_PATHS.avv },
  // Pflichtwörter aus §312k und §356a BGB — „ständig verfügbar" heißt: auf
  // jeder Seite, auch in der eingeloggten App.
  { key: 'footer.contracts_cancel', fallback: 'Verträge hier kündigen', to: CONTRACT_PATHS.cancel, lokal: true },
  { key: 'footer.contracts_withdraw', fallback: 'Vertrag widerrufen', to: CONTRACT_PATHS.withdraw, lokal: true },
  { key: 'footer.contact', fallback: 'Kontakt', href: CONTACT_URL },
];

/** Wohin ein interner Footer-Link zeigt: mit Präfix, wo es die Seite dort gibt. */
export function footerZiel(link: FooterEintrag & { to: string }, lokalisieren: (pfad: string) => string): string {
  return link.lokal ? lokalisieren(link.to) : link.to;
}

/**
 * Seiten, die auch im Wartungsmodus erreichbar bleiben: die Ziele der
 * Footer-Links (Rechtstexte, Kündigen/Widerrufen, Über uns) — mit und ohne
 * Sprach-Präfix. Der Wartungsbildschirm trägt den Legal-Footer (Regel #21);
 * ohne diese Ausnahme führte jeder seiner Links wieder auf ihn selbst.
 */
const RECHTS_PFAD = /^(?:\/[^/]+\/[^/]+)?\/(?:(?:legal|contracts)\/[^/]+|about)\/?$/;

export function istRechtsPfad(pfad: string): boolean {
  return RECHTS_PFAD.test(pfad);
}
